// components/chat-input-bar.js — SHARED CHAT INPUT BAR
// ═══════════════════════════════════════════════════════════
// Single source of truth for the chat input bar across the workstation:
// layer-1 chat panel, lens chat, and any future feature chat surface.
//
// Includes:
//   - Pending file pill (above the input)
//   - ContentEditable input with empty-state placeholder
//   - File upload button (left)
//   - Deep search toggle (left)  — web research routing
//   - Deep mode toggle (left)    — Opus + structured + verification
//   - Right action button: stop (loading) → send (input or file) → voice (empty)
//
// Any prop you don't pass hides its corresponding button. The default
// behavior matches the workstation chat panel exactly.
//
// USAGE:
//   <ChatInputBar
//     inputRef={inputRef}
//     value={input}
//     setValue={setInput}
//     onSend={() => submitMessage()}
//     loading={loading}
//     onAbort={abort}
//     pendingFile={pendingFile}
//     setPendingFile={setPendingFile}
//     onFileSelect={handleFilePickerSelect}
//     filesAtLimit={filesAtLimit}
//     deepSearchActive={deepSearchActive}
//     setDeepSearchActive={setDeepSearchActive}
//     deepModeArmed={deepModeArmed}
//     setDeepModeArmed={setDeepModeArmed}
//     voiceListening={voiceListening}
//     voiceEnabled={voiceEnabled}
//     onVoiceToggle={handleVoiceToggle}
//     placeholder="Ask Shift…"
//   />
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import FileUpload from './file-upload';
import AskShiftButton from './ask-shift-button';

// Paste threshold — above this, a big paste becomes a pinned
// preview pill instead of dumping into the input. Keeps the input
// scannable. ~400 chars or 8+ lines covers most "big block" pastes.
const PASTE_THRESHOLD_CHARS = 400;
const PASTE_THRESHOLD_LINES = 8;

export default function ChatInputBar({
  // ── input state (required) ──
  inputRef,
  value,
  setValue,
  onSend,
  loading,
  onAbort,

  // ── file (optional — hides upload + pill if onFileSelect not passed) ──
  pendingFile,
  setPendingFile,
  onFileSelect,
  filesAtLimit = false,

  // ── deep modes (optional — hides toggle if setter not passed) ──
  deepSearchActive = false,
  setDeepSearchActive,
  // Observer-mode Ask Shift toggle — when provided, renders the
  // animated antenna button in the icon row. Next Send will fire
  // the askShift path (response broadcasts to all participants).
  askShiftActive = false,
  onAskShiftToggle,
  deepModeArmed = false,
  setDeepModeArmed,

  // ── voice (optional — hides voice button if onVoiceToggle not passed) ──
  voiceListening = false,
  voiceEnabled = true,
  onVoiceToggle,

  // ── placeholder (optional — only the resting state; deep modes have built-in strings) ──
  placeholder = 'Message…',

  // ── input interception (optional — for slash commands, etc.) ──
  onInputChange,
}) {
  const showFileUpload = typeof onFileSelect === 'function';
  const showDeepSearch = typeof setDeepSearchActive === 'function';
  const showDeepMode = typeof setDeepModeArmed === 'function';
  const showVoice = typeof onVoiceToggle === 'function';

  // Big-paste preview — when a user pastes a block larger than the
  // threshold, hold it as a preview pill instead of dumping it into
  // the input. On Send the preview text is prepended to whatever the
  // user typed so the final message carries both.
  const [pastedText, setPastedText] = useState(null);

  function hasBigPaste(text) {
    if (!text) return false;
    if (text.length >= PASTE_THRESHOLD_CHARS) return true;
    const lineCount = (text.match(/\n/g) || []).length + 1;
    return lineCount >= PASTE_THRESHOLD_LINES;
  }

  function triggerSend() {
    if (loading) return;
    if (!value.trim() && !pendingFile && !pastedText) return;
    // Merge the pasted block in front of the typed message so the
    // recipient sees pasted content first, then any note the user
    // wrote to frame it.
    const merged = pastedText
      ? (value.trim() ? `${pastedText}\n\n${value}` : pastedText)
      : undefined;
    onSend(merged);
    if (pastedText) setPastedText(null);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      triggerSend();
    }
  }

  function handlePaste(e) {
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    if (hasBigPaste(text)) {
      // Hold the block as a preview pill — do NOT put it in the input.
      e.preventDefault();
      setPastedText(prev => prev ? `${prev}\n\n${text}` : text);
      return;
    }
    // Small paste — insert inline as plain text (strips HTML formatting).
    e.preventDefault();
    document.execCommand('insertText', false, text);
  }

  function clearPastedText() {
    setPastedText(null);
    if (inputRef?.current) inputRef.current.focus();
  }

  function clearPendingFile() {
    if (setPendingFile) setPendingFile(null);
    if (inputRef?.current) inputRef.current.focus();
  }

  return (
    <>
      {/* Pasted-text pill — shows when the user pasted a big block
          (over 400 chars or 8+ lines). The block is held here as a
          preview; on Send it's prepended to the typed message so the
          recipient sees both. Click × to drop the paste. */}
      {pastedText && (
        <div style={{
          padding: '6px 14px 0',
          flexShrink: 0,
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: '#141414',
            border: '1px solid #2a2a2a',
            borderRadius: 8,
            padding: '6px 10px',
            maxWidth: '100%',
          }}>
            <div style={{
              width: 36, height: 36,
              background: '#07070a',
              border: '1px solid #2a2a2a',
              borderRadius: 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 2.5h7l3 3V13a.5.5 0 0 1-.5.5H3a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5Z"
                  stroke="#888" strokeWidth="1.2" strokeLinejoin="round"/>
                <path d="M10 2.5V5a.5.5 0 0 0 .5.5H13M5 8h6M5 10.5h4"
                  stroke="#888" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
            </div>
            <div style={{
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}>
              <div style={{
                fontSize: 11,
                color: '#ccc',
                fontFamily: 'Georgia, serif',
                letterSpacing: '0.02em',
              }}>
                Pasted text
              </div>
              <div style={{
                fontSize: 10,
                color: '#666',
                fontFamily: 'Georgia, serif',
              }}>
                {pastedText.length.toLocaleString()} chars · {(pastedText.match(/\n/g) || []).length + 1} lines
              </div>
            </div>
            <button
              onClick={clearPastedText}
              title="Remove pasted content"
              style={{
                background: 'none',
                border: 'none',
                color: '#555',
                cursor: 'pointer',
                fontSize: 16,
                lineHeight: 1,
                padding: '0 4px',
                marginLeft: 4,
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#c46050'}
              onMouseLeave={e => e.currentTarget.style.color = '#555'}
            >×</button>
          </div>
        </div>
      )}

      {/* Pending file pill — sits above the input bar wrapper. The
          input wrapper drops its top border when the pill is showing
          so the two visually stack as one block. */}
      {pendingFile && (
        <div style={{
          padding: '6px 14px 0',
          flexShrink: 0,
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: '#141414',
            border: '1px solid #2a2a2a',
            borderRadius: 8,
            padding: '6px 10px',
          }}>
            {pendingFile.isImage ? (
              <img
                src={`data:${pendingFile.type};base64,${pendingFile.base64}`}
                style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 4, display: 'block' }}
                alt={pendingFile.name}
              />
            ) : (
              <div style={{
                width: 36,
                height: 36,
                background: '#07070a',
                border: '1px solid #2a2a2a',
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 9,
                color: '#666',
                fontFamily: 'Georgia, serif',
              }}>
                {(pendingFile.name || '').split('.').pop()?.toUpperCase() || 'FILE'}
              </div>
            )}
            <div style={{
              fontSize: 11,
              color: '#888',
              fontFamily: 'Georgia, serif',
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {pendingFile.name}
            </div>
            <span
              onClick={clearPendingFile}
              style={{
                cursor: 'pointer',
                color: '#555',
                fontSize: 16,
                lineHeight: 1,
                padding: '0 4px',
              }}
              onMouseEnter={e => e.currentTarget.style.color = '#c46050'}
              onMouseLeave={e => e.currentTarget.style.color = '#555'}
            >
              ×
            </span>
          </div>
        </div>
      )}

      {/* Input bar */}
      <div style={{
        padding: '10px 14px 14px',
        borderTop: pendingFile ? 'none' : '1px solid #1e1e2a',
        background: '#07070a',
        flexShrink: 0,
      }}>
        <div style={{
          background: '#141412',
          border: `1px solid ${(deepSearchActive || deepModeArmed || askShiftActive) ? 'rgba(200,169,110,0.3)' : '#252018'}`,
          borderRadius: 12,
          padding: '12px 14px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          transition: 'border-color 0.15s',
        }}>
          <div
            ref={inputRef}
            contentEditable
            suppressContentEditableWarning
            onPaste={handlePaste}
            onInput={e => {
              const val = e.currentTarget.innerText || '';
              setValue(val);
              if (onInputChange) onInputChange(val);
            }}
            onKeyDown={handleKeyDown}
            data-placeholder={
              askShiftActive ? 'Ask Shift…' :
              deepSearchActive ? 'Research deeply…' :
              deepModeArmed ? 'Deep mode…' :
              placeholder
            }
            className="chat-input-editable"
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: 'var(--text-1)',
              fontSize: 14,
              lineHeight: 1.6,
              fontFamily: 'Georgia, serif',
              width: '100%',
              whiteSpace: 'pre-wrap',
              minHeight: 24,
              maxHeight: 140,
              overflowY: 'auto',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Left actions: upload + deep search + deep mode */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {showFileUpload && (
                <FileUpload onFile={onFileSelect} disabled={filesAtLimit} />
              )}
              {showDeepSearch && typeof onAskShiftToggle !== 'function' && (
                <button
                  onClick={() => {
                    const next = !deepSearchActive;
                    setDeepSearchActive(next);
                    if (next && setDeepModeArmed) setDeepModeArmed(false);
                    if (next && inputRef?.current) inputRef.current.focus();
                  }}
                  title="Deep search the web"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}
                  onMouseEnter={e => { if (!deepSearchActive) e.currentTarget.querySelectorAll('circle, path').forEach(el => el.setAttribute('stroke', '#7a9e82')); }}
                  onMouseLeave={e => { if (!deepSearchActive) e.currentTarget.querySelectorAll('circle, path').forEach(el => el.setAttribute('stroke', '#888888')); }}
                >
                  <svg width="20" height="20" viewBox="0 0 18 18" fill="none">
                    <circle cx="8" cy="8" r="5.5" stroke={deepSearchActive ? '#c8a96e' : '#888888'} strokeWidth="1.4" />
                    <path d="M12.5 12.5L16 16" stroke={deepSearchActive ? '#c8a96e' : '#888888'} strokeWidth="1.4" strokeLinecap="round" />
                    <path d="M8 5.5v5M5.5 8h5" stroke={deepSearchActive ? '#c8a96e' : '#888888'} strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                </button>
              )}
              {showDeepMode && (
                <button
                  onClick={() => {
                    const next = !deepModeArmed;
                    setDeepModeArmed(next);
                    if (next && setDeepSearchActive) setDeepSearchActive(false);
                    if (next && inputRef?.current) inputRef.current.focus();
                  }}
                  title={deepModeArmed
                    ? 'Deep mode armed — your next message will route through Opus + structured output + verification'
                    : 'Deep mode — Opus + structured output + verification on the next message'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}
                  onMouseEnter={e => { if (!deepModeArmed) e.currentTarget.querySelectorAll('circle').forEach(el => el.setAttribute('stroke', '#7a9e82')); }}
                  onMouseLeave={e => { if (!deepModeArmed) e.currentTarget.querySelectorAll('circle').forEach(el => el.setAttribute('stroke', '#888888')); }}
                >
                  <svg width="20" height="20" viewBox="0 0 18 18" fill="none">
                    <circle cx="9" cy="9" r="6.5" stroke={deepModeArmed ? '#c8a96e' : '#888888'} strokeWidth="1.4" />
                    <circle cx="9" cy="9" r="2.6" fill={deepModeArmed ? '#c8a96e' : '#888888'} stroke="none" />
                  </svg>
                </button>
              )}
              {/* Observer-mode Ask Shift — antenna + label. Positioned
                  AFTER deep mode per Tim's request so the activation
                  affordances read left-to-right: upload, deep mode,
                  ask Shift. */}
              {typeof onAskShiftToggle === 'function' && (
                <AskShiftButton
                  onAskShift={onAskShiftToggle}
                  active={askShiftActive}
                  disabled={loading}
                />
              )}
            </div>

            {/* Right action: stop / send / voice */}
            {loading ? (
              <button
                onClick={onAbort}
                title="Stop"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: '#1e1e2a',
                  border: '1px solid #333',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <rect width="8" height="8" rx="1.5" fill="#888" />
                </svg>
              </button>
            ) : (value.trim() || pendingFile || pastedText) ? (
              <button
                onClick={triggerSend}
                title="Send"
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: '#c8a96e',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                  <path d="M9 14V4" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" />
                  <path d="M4.5 8.5L9 4L13.5 8.5" stroke="#0a0a0a" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ) : showVoice ? (
              <button
                onClick={onVoiceToggle}
                title={voiceListening ? 'Stop voice' : 'Start voice'}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: '50%',
                  background: voiceListening ? '#c8a96e' : '#1e1e2a',
                  border: voiceListening ? 'none' : '1px solid #333',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={e => {
                  if (!voiceListening) {
                    e.currentTarget.style.borderColor = '#7a9e82';
                    e.currentTarget.querySelectorAll('rect, path').forEach(el => el.setAttribute('stroke', '#7a9e82'));
                  }
                }}
                onMouseLeave={e => {
                  if (!voiceListening) {
                    e.currentTarget.style.borderColor = '#333';
                    e.currentTarget.querySelectorAll('rect, path').forEach(el => el.setAttribute('stroke', '#888888'));
                  }
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <rect x="9" y="2" width="6" height="12" rx="3"
                    stroke={voiceListening ? '#07070a' : '#888888'} strokeWidth="1.5" />
                  <path d="M5 11a7 7 0 0014 0"
                    stroke={voiceListening ? '#07070a' : '#888888'} strokeWidth="1.5" strokeLinecap="round" />
                  <path d="M12 18v4"
                    stroke={voiceListening ? '#07070a' : '#888888'} strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
            ) : (
              // No voice wired — render an inert spacer so the row keeps
              // its alignment when the input is empty.
              <div style={{ width: 30, height: 30 }} />
            )}
          </div>
        </div>
      </div>

      {/* Placeholder rendering for the contentEditable input. The
          chat panel previously set data-placeholder without a CSS
          rule, so the placeholder never showed. This block is the
          fix — global so any contentEditable input that uses the
          same className renders its placeholder consistently. */}
      <style jsx global>{`
        .chat-input-editable[data-placeholder]:empty::before {
          content: attr(data-placeholder);
          color: #555;
          font-style: italic;
          pointer-events: none;
        }
      `}</style>
    </>
  );
}
